import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { User, Building2 } from "lucide-react";
import { StudentsContext, CustomersContext } from "../../Context";
import PhoneNumber from "../content/components/PhoneNumber";
import HtClass from "../content/components/HtClass";
import "./topbar-premium.css";

/**
 * Search results dropdown for students and customers.
 * Displays matching search results with premium flat styling.
 * Uses lucide-react icons instead of emojis.
 *
 * @param {object} props Component props
 * @param {string} props.search Current search value
 * @param {function} props.setSearch Function to set search value
 * @author Forge
 * @date 2026-04-07
 * @version 4.0
 */
export default function SearchStudents(props) {
  const { search } = props;
  const { customers } = useContext(CustomersContext) || { customers: [] };
  const { students } = useContext(StudentsContext) || { students: [] };

  const filteredStudents = students
    .filter(
      (s) =>
        s.name?.toLowerCase().includes(search.toLowerCase()) ||
        s.phone?.includes(search)
    )
    .sort((a, b) => a.name.localeCompare(b.name) && a.status.localeCompare(b.status))
    .slice(0, 50);

  const filteredCustomers = customers
    .filter(
      (c) =>
        c.ref?.toLowerCase().includes(search.toLowerCase()) ||
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.phone?.includes(search)
    )
    .slice(0, 50);

  if (filteredStudents.length === 0 && filteredCustomers.length === 0) {
    return (
      <div className="search-dropdown">
        <div className="search-dropdown-empty">
          <svg className="search-dropdown-empty-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="search-dropdown-empty-text">No results found for "{search}"</p>
        </div>
      </div>
    );
  }

  return (
    <div className="search-dropdown">
      {filteredStudents.length > 0 && (
        <>
          <div className="search-dropdown-section-header">
            Students
          </div>
          <div className="search-dropdown-section">
            {filteredStudents.map((s, index) => (
              <Link
                to={`/student/${s._id}`}
                className="search-dropdown-item"
                key={`student-${index}`}
                onClick={() => props.setSearch("")}
              >
                <div className="search-dropdown-item-icon">
                  <User size={16} />
                </div>
                <div className="search-dropdown-item-content">
                  <div className="search-dropdown-item-name">{s.name}</div>
                  <div className="search-dropdown-item-phone">
                    <PhoneNumber value={s.phone} />
                  </div>
                  <div className="search-dropdown-item-subtitle">
                    <span className={`search-dropdown-item-badge ${getStatusClass(s.status)}`}>
                      {s.status}
                    </span>
                    {s.classes?.map((classId, classIndex) => (
                      <HtClass
                        key={`class-${classIndex}`}
                        htClass={classId}
                        link={false}
                        style={{ display: 'inline-block' }}
                      />
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {filteredCustomers.length > 0 && (
        <>
          <div className="search-dropdown-section-header">
            Customers
          </div>
          <div className="search-dropdown-section">
            {filteredCustomers.map((c, index) => (
              <Link
                to={`/customer/${c._id}`}
                className="search-dropdown-item"
                key={`customer-${index}`}
                onClick={() => props.setSearch("")}
              >
                <div className="search-dropdown-item-icon">
                  <Building2 size={16} />
                </div>
                <div className="search-dropdown-item-content">
                  <div className="search-dropdown-item-name">{c.name}</div>
                  <div className="search-dropdown-item-subtitle">
                    <span className="search-dropdown-item-badge">
                      {c.ref}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function getStatusClass(status) {
  if (status === "Stopped" || status === "Lead") {
    return "error";
  }
  if (status === "Pending") {
    return "warning";
  }
  return "success";
}
